import { useCallback } from 'react'

import {
  BuilderDroppableElement,
  IActionRepeaterValue,
  IActionType,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import { ColorVariant } from '@makyoapp/makyo-tw-ui'

import { IViewComponent } from '@/core/app-builder/app/component'
import { IAppLayer } from '@/core/app-builder/app/layer'
import { IDBTable } from '@/core/db'
import constate from '@/lib/constate'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useCompSettings } from '@/hooks/__project__/useCompSettings'
import { useLoadCompCustomFonts } from '@/hooks/__project__/useLoadCompCustomFonts'
import { SectionBackgroundGroupProps } from '@/shared/builder/default/section.default'
import { ComponentSettingKey, FormComponentProps } from '@/shared/builder/types'
import { EVENT_KEYS } from '@/shared/custom-event'
import { isEmpty } from '@/shared/utils'

const defaultFormProps = {
  settings: {},
  formId: '',
  formState: {},
  setFormState: () => {},
  formErrors: {},
  setFormErrors: () => {},
  requiredFieldsStatus: {},
  setRequiredFieldsStatus: () => {},
  dataSource: '',
  dataSourceTable: undefined,
}

interface CompContextProviderProps {
  component: IViewComponent
  containerClient: IAppLayer
  rowId?: string
  detailRowIndex?: number
  ancestors: BuilderDroppableElement[]
  formProps?: FormComponentProps
}

/**
 * This hook is used internally within each builder component to access its container (parent) and its own IViewComponent instance
 *
 * @todo extract some of complex logic into separate hooks
 */
const [CompContextProvider, useCompContext] = constate(
  'CompContext',
  ({
    component,
    containerClient,
    rowId,
    detailRowIndex,
    ancestors = [],
    formProps = defaultFormProps,
  }: CompContextProviderProps) => {
    // Get reactive component settings
    const settings = useCompSettings(component)

    // // load table ids from component settings
    // useLoadCompSettingsTableIds(settings)

    // load custom fonts from component settings
    useLoadCompCustomFonts(settings)

    // Additional State for details page/overlay
    const detailTableId = containerClient.settings._TableID

    // Get ancestor settings
    const latestAncestor = ancestors[ancestors.length - 1]
    const hasAncestor = !!latestAncestor && !!latestAncestor.id
    const ancestorComp = hasAncestor ? containerClient.componentsMap.get(latestAncestor.id) : undefined
    const ancestorSettings = useCompSettings(ancestorComp)

    return {
      /**
       * Current IViewComponent
       */
      component,
      /**
       * Reactive component settings
       */
      settings,
      ancestors,
      containerClient,
      isDetailPage: !!rowId,
      rowId,
      detailTableId,
      detailRowIndex,
      /**
       * Ancestor settings
       */
      latestAncestor,
      ancestorSettings,
      /**
       * Form reactive props, only access it within component that has form parent
       */
      formProps,
    }
  }
)
export { CompContextProvider, useCompContext }

/** utility function to find the parent background color */
function findParentBgColor(
  elId: string,
  parents: BuilderDroppableElement[],
  allSettings: Record<string, any>,
  mode: 'light' | 'dark'
): ColorVariant | undefined {
  const parentEl = parents.find((el) => el.id === elId)
  if (!parentEl) return

  const parentSettings = allSettings[parentEl.id]
  if (!parentSettings) return

  switch (parentEl.compId) {
    /**
     * @note section parent only support solid color for now,
     * in the future, we will add support for gradient color & image
     */
    case 'section': {
      // get background settings
      const bgSettings = parentSettings[ComponentSettingKey.SectionDesignBackground] as SectionBackgroundGroupProps[]
      const bgColorValues = bgSettings?.find((item) => item.type === 'color')?.values
      if (!bgColorValues) return

      const bgMode = (bgColorValues?.mode as any)[mode]

      if (bgMode === 'No Color') {
        if (!parentEl.parentId) return
        return findParentBgColor(parentEl.parentId, parents, allSettings, mode)
      }

      // get solid color
      const bgSolidColor = bgColorValues.color as ColorVariant | { light: ColorVariant; dark: ColorVariant }
      if (!bgSolidColor) return

      if (typeof bgSolidColor === 'object' && 'light' in bgSolidColor && 'dark' in bgSolidColor) {
        return bgSolidColor[mode]
      }
      return bgSolidColor
    }
    /**
     * @note currently form doesnt have bg color settings
     */
    case 'form': {
      if (!parentEl.parentId) return
      return findParentBgColor(parentEl.parentId, parents, allSettings, mode)
    }
    case 'tab-item': {
      const tabEl = parentEl

      // get parent tabs el
      const tabItemParent = parents.find((el) => el.id === tabEl.parentId)
      if (!tabItemParent) return

      // get parent tabs settings
      const tabItemParentSettings = allSettings[tabItemParent.id] as Record<string, any>
      if (!tabItemParentSettings) return

      // get parent tabs color settings
      const tabItemParentBg = tabItemParentSettings[ComponentSettingKey.TabsDesignBgColor] ?? 'primary-90'
      return tabItemParentBg as ColorVariant
    }
  }
}

/**
 * New hook to get the background color of the current component
 */
export const useCompBackgroundColor = () => {
  const { latestAncestor, ancestors, containerClient } = useCompContext()
  const [colorScheme] = useBuilderColorSchemeV2()

  const allSettings = containerClient.settings._BodySettings || {}
  const defaultPageBg = (containerClient.settings._Settings._BackgroundColor ?? 'primary-95') as ColorVariant

  // by default use page background color
  let currentParentBg = defaultPageBg as ColorVariant

  if (!!latestAncestor) {
    currentParentBg = findParentBgColor(latestAncestor.id, ancestors, allSettings, colorScheme.current) ?? defaultPageBg
  }

  return { currentParentBg }
}

/**
 * @deprecated use useCompBackgroundColor instead
 */
export const useAdaptiveTextColor = () => {
  return useCompBackgroundColor()
}

/**
 * This hook is used to access form context of the current component
 */
export const useCompFormContext = () => {
  // access comp context & form props
  const {
    rowId,
    component,
    formProps: {
      settings: compFormSettings,
      dataSource,
      dataSourceTable,
      formId,
      formErrors,
      formState,
      requiredFieldsStatus,
      setFormErrors,
      setFormState,
      setRequiredFieldsStatus,
    },
  } = useCompContext()

  // access action context
  const { actionLoadingMap, simulateAction } = useBuilderActionContextV2()
  const isLoading = actionLoadingMap.get(component._id) || false

  // get parent background color
  const { currentParentBg } = useCompBackgroundColor()

  // form design
  const cornerRadius = compFormSettings[ComponentSettingKey.FormDesignFieldShape] ?? 'medium'
  const typoSettings = (compFormSettings[ComponentSettingKey.FormDesignTypography] ?? {}) as TypographyGroupValue
  const labelTypo = typoSettings[ComponentSettingKey.FormDesignLabelTypography] ?? { typography: 'label-medium' }
  const inputTypo = typoSettings[ComponentSettingKey.FormDesignInputTypography] ?? { typography: 'body-medium' }
  const helpTypo = typoSettings[ComponentSettingKey.FormDesignHelpTypography] ?? { typography: 'body-small' }

  // fire event to reset form
  const handleResetForm = useCallback(() => {
    window.dispatchEvent(new CustomEvent(EVENT_KEYS.BUILDER_FORM_RESET, { detail: { formId } }))
  }, [formId])

  // handle form submit
  const handleFormSubmit = useCallback(
    async (actions: IActionRepeaterValue[]) => {
      if (isEmpty(actions)) return

      const submitActions = (actions || []).map((action) => {
        if ([IActionType.FormAddRow, IActionType.FormUpdateRow].includes(action.type)) {
          action.settings = {
            ...action.settings,
            tableId: dataSource,
            data: formState,
          }
        }
        return action
      })

      const includeFormSubmit = submitActions.some(
        (action) => action.type === IActionType.FormAddRow || action.type === IActionType.FormUpdateRow
      )

      await simulateAction({
        actions: submitActions,
        rowId,
        triggerSource: {
          id: component._id,
          label: component._Title || component._ComponentID,
          type: 'component',
        },
      })

      if (includeFormSubmit) {
        handleResetForm()
      }
    },
    [dataSource, formState, handleResetForm, rowId, simulateAction, component]
  )

  const setRequiredFieldStatus = useCallback(
    (fieldId: string, isEmpty: boolean) => {
      setRequiredFieldsStatus?.((prev) => ({ ...prev, [fieldId]: isEmpty }))
    },
    [setRequiredFieldsStatus]
  )

  const formSettings = {
    dataSource,
    labelTypo,
    inputTypo,
    helpTypo,
    bgColor: currentParentBg,
    cornerRadius,
  }

  return {
    dataSource,
    dataSourceTable: dataSourceTable as IDBTable,
    formId,
    formState,
    setFormState,
    formErrors,
    setFormErrors,
    requiredFieldsStatus,
    setRequiredFieldStatus,
    formSettings,
    handleFormSubmit,
    handleResetForm,
    isFormLoading: isLoading,
  }
}
